import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { CheckCircle2, Circle } from "lucide-react";
import type { Schemas } from "../api/client";
import { Page, useApiQuery } from "../app/Page";
import { PhoneLineDialog } from "../app/PhoneLineDialog";
import { useFormat, useT, type MessageId } from "../i18n";
import { ErrorState, Loading } from "../ui/states";

type Onboarding = Schemas["Onboarding"];

interface Step {
  label: MessageId;
  hint: MessageId;
  to: string;
  hash?: string;
  at: (o: Onboarding) => string | null | undefined;
}

/** The first run (design 11 §3.1, V1.1-EA-T03), in the order a new tenant goes: a key, an agent, talking to it in
 * the browser (no phone line needed), then the phone side. Each step is done when the tenant's data says so. */
const STEPS: Step[] = [
  { label: "home.step.key", hint: "home.hint.key", to: "/developers", hash: "credentials", at: (o) => o.first_key_at },
  { label: "home.step.agent", hint: "home.hint.agent", to: "/agents", at: (o) => o.first_agent_at },
  { label: "home.step.test", hint: "home.hint.test", to: "/agents", at: (o) => o.first_test_at },
  { label: "home.step.compliance", hint: "home.hint.compliance", to: "/compliance", at: (o) => o.compliance_profile && o.created_at },
  { label: "home.step.trunk", hint: "home.hint.trunk", to: "/trunks", at: (o) => o.first_line_at },
  { label: "home.step.number", hint: "home.hint.number", to: "/numbers", at: (o) => o.first_number_at },
  { label: "home.step.call", hint: "home.hint.call", to: "/calls", at: (o) => o.first_call_at },
];

export function HomePage() {
  const t = useT();
  const format = useFormat();
  const query = useApiQuery("onboarding", (api) => api.GET("/v1/tenant/onboarding"));
  const onboarding = query.data;
  const next = onboarding ? STEPS.find((s) => !s.at(onboarding)) : undefined;
  const [needsLine, setNeedsLine] = useState(false);
  return (
    <Page title={t("home.title")} description={t("home.intro")}>
      <PhoneLineDialog open={needsLine} onOpenChange={setNeedsLine} />
      {query.isPending && <Loading label={t("app.loading")} />}
      {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}
      {onboarding && (
        <ol aria-label={t("home.steps")} className="max-w-xl space-y-2">
          {STEPS.map((step) => {
            const at = step.at(onboarding);
            const className = `flex w-full items-center gap-3 rounded-lg border p-4 text-left hover:bg-[var(--color-surface)] ${
              step === next ? "border-[var(--color-primary)]" : "border-[var(--color-border)]"
            }`;
            const content = (
              <>
                  {at ? (
                    <CheckCircle2 aria-hidden="true" className="shrink-0 text-[var(--color-success)]" />
                  ) : (
                    <Circle aria-hidden="true" className="shrink-0 text-[var(--color-muted)]" />
                  )}
                  <span className="flex-1">
                    <span className="block font-medium">
                      {step.label === "home.step.compliance" && onboarding.compliance_profile
                        ? t("home.step.complianceIs", { profile: onboarding.compliance_profile })
                        : t(step.label)}
                    </span>
                    {!at && <span className="block text-sm text-[var(--color-muted)]">{t(step.hint)}</span>}
                  </span>
                  <span className="text-sm text-[var(--color-muted)]">
                    {at ? t("home.step.doneAt", { when: format.dateTime(at) }) : t("home.step.todo")}
                  </span>
              </>
            );
            return (
              <li key={step.label}>
                {/* V1.2-UX-T06: no line yet: how to get one, in a dialog, rather than a page to work out alone */}
                {step.label === "home.step.trunk" && !at ? (
                  <button type="button" onClick={() => setNeedsLine(true)} aria-current={step === next ? "step" : undefined}
                    className={className}>{content}</button>
                ) : (
                  <Link to={step.to} hash={step.hash} aria-current={step === next ? "step" : undefined} className={className}>
                    {content}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </Page>
  );
}
